import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import type { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import type { ApiSingleResponseWithData, PaginatedResult } from '../../interfaces/api-response.interface';
import { TicketCategorie, type SupportTicketDto, type TicketStatut } from '../../interfaces/support.interface';

export type SupportTicketType = 'bug' | 'feature' | 'improvement';

/**
 * Correspondance entre le type d'entrée du menu support (chaîne) et la catégorie
 * de ticket (enum numérique attendu par la page support via le query param `type`).
 */
export const SUPPORT_TYPE_TO_CATEGORIE: Record<SupportTicketType, TicketCategorie> = {
  bug:         TicketCategorie.Bug,
  feature:     TicketCategorie.Fonctionnalite,
  improvement: TicketCategorie.Amelioration,
};

export interface CreateSupportTicketCommand {
  sujet:        string;
  message:      string;
  categorie:    TicketCategorie;
  attachments?: File[];
}

export interface SupportTicketsParams {
  page?:     number;
  pageSize?: number;
  statut?:   TicketStatut;
}

const BASE          = `${environment.baseUrl}support/tickets`;
/** Adresse du support Clésame (source unique, réutilisée là où l'API n'est pas joignable). */
export const SUPPORT_EMAIL = 'support@clesame.fr';

@Injectable({ providedIn: 'root' })
export class SupportService {
  private http = inject(HttpClient);

  createTicket(cmd: CreateSupportTicketCommand): Observable<void> {
    const form = new FormData();
    form.append('Sujet',     cmd.sujet);
    form.append('Message',   cmd.message);
    form.append('Categorie', String(cmd.categorie));
    cmd.attachments?.forEach(file => form.append('Attachments', file, file.name));
    return this.http.post<void>(BASE, form);
  }

  getTickets(params?: SupportTicketsParams): Observable<ApiSingleResponseWithData<PaginatedResult<SupportTicketDto>>> {
    let httpParams = new HttpParams();
    if (params?.page     !== undefined) httpParams = httpParams.set('page',     params.page);
    if (params?.pageSize !== undefined) httpParams = httpParams.set('pageSize', params.pageSize);
    if (params?.statut   !== undefined) httpParams = httpParams.set('statut',   params.statut);
    return this.http.get<ApiSingleResponseWithData<PaginatedResult<SupportTicketDto>>>(BASE, { params: httpParams });
  }

  getTicketById(id: number): Observable<ApiSingleResponseWithData<SupportTicketDto>> {
    return this.http.get<ApiSingleResponseWithData<SupportTicketDto>>(`${BASE}/${id}`);
  }

  /**
   * Fallback: opens the default mail client when the API call fails.
   */
  openMailtoFallback(sujet: string, message: string, categorie: TicketCategorie): void {
    const categorieLabel: Record<number, string> = {
      [TicketCategorie.Bug]:            '[Bug]',
      [TicketCategorie.Fonctionnalite]: '[Nouvelle fonctionnalité]',
      [TicketCategorie.Amelioration]:   '[Amélioration]',
      [TicketCategorie.Question]:       '[Question]',
      [TicketCategorie.Autre]:          '[Autre]',
    };
    const label   = categorieLabel[categorie] ?? '[Demande]';
    const subject = encodeURIComponent(`${label} ${sujet}`);
    const body    = encodeURIComponent(
      [
        `Catégorie : ${label}`,
        `Objet : ${sujet}`,
        '',
        'Message :',
        message,
        '',
        `Page : ${window.location.href}`,
        `Navigateur : ${navigator.userAgent}`,
      ].join('\n')
    );
    window.open(`mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`, '_blank');
  }
}
